// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Button,
    ContentLoader,
    Heading,
    Icon,
    Item,
    Label,
    Link,
    Paragraph,
    Text,
    TextArea,
    Toolbar,
    ToolbarExpander,
    useTranslate,
} from '@plesk/ui-library';
import { Locale, Tooltip } from 'jsw';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useLocalStorage } from 'common/hooks';

import { Answer, CopilotContext } from '../../CopilotContext';
import AiStylePicker, { AiStyle } from './AiStylePicker';
import ConversationHistory from './ConversationHistory';
import MessageItem from 'components/layouts/PageLayout/PageHeader/Copilot/MessageItem';
import useChatScrollRestoration from './useChatScrollRestoration';
import { execute } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import COPILOT_CHAT_QUERY from 'queries/CopilotChat.graphql';
import GEN_COPILOT_ANSWER from 'queries/GenCopilotAnswer.graphql';
import GEN_COPILOT_ANSWER_STREAM from 'queries/GenCopilotAnswerStream.graphql';
import SEND_COPILOT_FEEDBACK from 'queries/SendCopilotFeedback.graphql';

enum Role {
    User = 'user',
    Assistant = 'assistant',
}

const SUGGESTIONS = [
    { key: 'welcome.suggest.domain', icon: 'globe' },
    { key: 'welcome.suggest.upload-files', icon: 'arrow-up-tray' },
    { key: 'welcome.suggest.wordpress', icon: 'wordpress' },
    { key: 'welcome.suggest.security', icon: 'shield' },
    { key: 'welcome.suggest.email-phone', icon: 'mail' },
    { key: 'welcome.suggest.spam', icon: 'mail-circle-filled' },
    { key: 'welcome.suggest.slow-website', icon: 'monitoring' },
] as const;

type StreamChunk = {
    content: string;
    chatId: string | null;
    messageId: number | null;
    ableToAnswer: boolean | null;
};

// Initial-payload data of the GenCopilotAnswerStream mutation; subsequent
// incremental chunks are typed by GraphQL17Alpha9Handler.TypeOverrides
type GenCopilotAnswerData = {
    genCopilotAnswer?: {
        error: string | null;
        contentChunks?: (StreamChunk | null)[] | null;
    } | null;
};

const STREAM_OBJECT_DELAY_MS = 10;
const Translate = Locale.getTranslate('components.copilot');

const Chat = () => {
    const translate = useTranslate('components.copilot');
    const {
        setIsRightSidebarOpen,
        isRightSidebarOpen,
        loading: historyLoading,
        answers, setAnswers,
        chatId, setChatId,
        startNewChat,
    } = useContext(CopilotContext);

    const { data: { config } = {} } = useQuery(COPILOT_CHAT_QUERY);
    const useStream = config?.copilot?.chat?.useStream ?? true;

    const [genAnswer] = useMutation(GEN_COPILOT_ANSWER);
    const client = useApolloClient();

    const isRightSidebarOpenRef = useRef(isRightSidebarOpen);
    const isHistoryOpenRef = useRef(false);

    const [question, setQuestion] = useState('');
    const [selectedStyle, setSelectedStyle] = useState<AiStyle | null>(null);
    const [isHistoryOpen, setIsHistoryOpen] = useLocalStorage('isCopilotHistoryOpen', false);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLFormElement>(null);

    const [loading, setLoading] = useState(false);
    const nextIdRef = useRef(0);
    useEffect(() => {
        nextIdRef.current = answers.reduce((max, a) => Math.max(max, a.id + 1), 0);
    }, [answers]);

    const [feedbackDisabled, setFeedbackDisabled] = useState<Set<Answer['id']>>(new Set());
    const [sendFeedback] = useMutation(SEND_COPILOT_FEEDBACK);

    const setAnswersAndFocus = (newAnswers: Answer[]) => {
        setAnswers(newAnswers);
        setTimeout(() => {
            scrollToBottom();
            inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
        });
    };

    const scrollToBottom = () => {
        const listEl = listRef.current;
        if (listEl) {
            listEl.scrollTop = listEl.scrollHeight;
        }
    };

    const setContentRef = useChatScrollRestoration({ listRef, chatId });

    useEffect(() => {
        if (isRightSidebarOpen && !isRightSidebarOpenRef.current) {
            inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
        }
        isRightSidebarOpenRef.current = isRightSidebarOpen;
    }, [isRightSidebarOpen]);

    useEffect(() => {
        let focusTimeout: ReturnType<typeof setTimeout> | undefined;
        if (!isHistoryOpen && isHistoryOpenRef.current) {
            focusTimeout = setTimeout(() => {
                if (isRightSidebarOpenRef.current && !isHistoryOpenRef.current) {
                    inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
                }
            });
        }
        isHistoryOpenRef.current = isHistoryOpen;
        return () => {
            if (focusTimeout) {
                clearTimeout(focusTimeout);
            }
        };
    }, [isHistoryOpen]);

    useEffect(() => {
        const container = listRef.current;
        if (!container) {
            return;
        }

        const showTooltip = (e: Event) => {
            const el = e.target as HTMLElement;
            if (el.tagName === 'A' && el.hasAttribute('data-title')) {
                const text = el.getAttribute('data-title');
                if (!text) {
                    return;
                }
                Tooltip.init(el, { text });
                Tooltip.set({ element: el, config: { text } });
            }
        };

        container.addEventListener('focus', showTooltip, true);
        container.addEventListener('mouseenter', showTooltip, true);

        return () => {
            container.removeEventListener('focus', showTooltip, true);
            container.removeEventListener('mouseenter', showTooltip, true);
        };
    }, []);

    const processNonStreamResponse = async (
        askQuestion: string,
        newAnswers: Answer[],
    ) => {
        try {
            const result = await genAnswer({ variables: { input: { question: askQuestion, selectedStyle, chatId } } });
            const data = result.data?.genCopilotAnswer;
            if (data?.chatId && data.chatId !== chatId) {
                setChatId(data.chatId);
            }
            const answersWithAnswer: Answer[] = [
                ...newAnswers,
                {
                    role: Role.Assistant,
                    content: data?.content ?? undefined,
                    error: data?.error ?? undefined,
                    id: nextIdRef.current++,
                    able_to_answer: data?.ableToAnswer ?? false,
                    chat_id: data?.chatId ?? undefined,
                    message_id: data?.messageId ?? undefined,
                },
            ];
            setAnswersAndFocus(answersWithAnswer);
        } catch {
            const answersWithError: Answer[] = [
                ...newAnswers,
                {
                    role: Role.Assistant,
                    error: String(translate('service-unavailable')),
                    id: nextIdRef.current++,
                },
            ];
            setAnswersAndFocus(answersWithError);
        }
        setLoading(false);
    };

    const processStreamResponse = async (
        askQuestion: string,
        newAnswers: Answer[],
    ) => {
        setLoading(true);
        setTimeout(() => scrollToBottom());

        // useMutation() resolves only once the whole response has arrived, so the
        // link chain is executed directly to observe each incremental payload
        const stream = {
            queue: [] as StreamChunk[],
            done: false,
            error: undefined as string | undefined,
            wake: () => {},
        };

        execute(client.link, {
            query: GEN_COPILOT_ANSWER_STREAM,
            variables: { input: { question: askQuestion, stream: true, selectedStyle, chatId } },
            context: { multipartStream: true },
        }, { client }).subscribe({
            next: result => {
                if ('data' in result) {
                    const initial = (result.data as GenCopilotAnswerData | null | undefined)?.genCopilotAnswer;
                    if (initial?.error) {
                        stream.error = initial.error;
                    }
                    initial?.contentChunks?.forEach(chunk => chunk && stream.queue.push(chunk));
                }
                if ('incremental' in result) {
                    result.incremental?.forEach(entry => {
                        if ('items' in entry) {
                            (entry.items as unknown as (StreamChunk | null)[]).forEach(chunk => chunk && stream.queue.push(chunk));
                        }
                    });
                }
                if ('errors' in result && result.errors?.length) {
                    stream.error ??= String(translate('service-unavailable'));
                }
                stream.wake();
            },
            error: () => {
                stream.error ??= String(translate('service-unavailable'));
                stream.done = true;
                stream.wake();
            },
            complete: () => {
                stream.done = true;
                stream.wake();
            },
        });

        let lastAnswer = newAnswers.length > 0 ? newAnswers[newAnswers.length - 1] : undefined;
        let noResponse = true;
        let loadingCleared = false;

        while (!stream.done || stream.queue.length > 0) {
            if (stream.queue.length === 0) {
                await new Promise<void>(resolve => {
                    stream.wake = resolve;
                });
                stream.wake = () => {};
                continue;
            }

            const typing = !document.hidden;
            // When the tab is visible chunks are applied one by one for smooth text
            // streaming; in a hidden tab they are applied in a single batch
            const chunks = typing ? [stream.queue.shift() as StreamChunk] : stream.queue.splice(0);

            for (const chunk of chunks) {
                noResponse = false;
                if (chunk.chatId && chunk.chatId !== chatId) {
                    setChatId(chunk.chatId);
                }
                if (lastAnswer && lastAnswer.role === Role.Assistant) {
                    lastAnswer = {
                        ...lastAnswer,
                        content: (lastAnswer.content ?? '') + chunk.content,
                        chat_id: lastAnswer.chat_id ?? chunk.chatId ?? undefined,
                        message_id: lastAnswer.message_id ?? chunk.messageId ?? undefined,
                        able_to_answer: lastAnswer.able_to_answer || (chunk.ableToAnswer ?? false),
                    };
                    newAnswers[newAnswers.length - 1] = lastAnswer;
                } else {
                    lastAnswer = {
                        role: Role.Assistant,
                        content: chunk.content,
                        id: nextIdRef.current++,
                        chat_id: chunk.chatId ?? undefined,
                        message_id: chunk.messageId ?? undefined,
                        able_to_answer: chunk.ableToAnswer ?? false,
                        streaming: true,
                    };
                    newAnswers.push(lastAnswer);
                }
            }

            setAnswersAndFocus([...newAnswers]);
            if (!loadingCleared) {
                setLoading(false);
                loadingCleared = true;
            }
            if (typing) {
                // A short delay for smoother text streaming in the UI
                await new Promise(resolve => setTimeout(resolve, STREAM_OBJECT_DELAY_MS));
            }
        }

        if (noResponse) {
            newAnswers.push({
                role: Role.Assistant,
                error: stream.error ?? String(translate('service-unavailable')),
                id: nextIdRef.current++,
            });
            setAnswersAndFocus([...newAnswers]);
        } else {
            const last = newAnswers[newAnswers.length - 1];
            if (last?.role === Role.Assistant) {
                newAnswers[newAnswers.length - 1] = { ...last, streaming: false };
                setAnswersAndFocus([...newAnswers]);
            }
        }
        setLoading(false);
    };

    const loadAnswer = async (askQuestion = question) => {
        const trimmed = askQuestion.trim();
        if (!trimmed || loading) {
            return;
        }
        const newAnswers: Answer[] = [...answers, { role: Role.User, content: trimmed, id: nextIdRef.current++ }];
        setAnswers(newAnswers);
        setTimeout(() => scrollToBottom());

        setQuestion('');

        if (!useStream) {
            setLoading(true);
            setTimeout(() => scrollToBottom());
            await processNonStreamResponse(trimmed, newAnswers);
        } else {
            await processStreamResponse(trimmed, newAnswers);
        }
    };


    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        loadAnswer();
    };

    const handleFeedback = async (messageId: Answer['id'], value: Answer['feedback']) => {
        const targetAnswer = answers.find(a => a.id === messageId);
        if (targetAnswer?.message_id == null || targetAnswer?.chat_id == null) {
            return;
        }

        setFeedbackDisabled(prev => new Set(prev).add(messageId));
        setTimeout(() => {
            setFeedbackDisabled(prev => {
                const next = new Set(prev);
                next.delete(messageId);
                return next;
            });
        }, 3000);

        const initialFeedbackValue = targetAnswer.feedback;
        const isToggleOff = initialFeedbackValue === value;
        const newValue = isToggleOff ? undefined : value;

        updateAnswerFeedback(messageId, newValue);

        try {
            await sendFeedback({
                variables: {
                    input: {
                        chatId: targetAnswer.chat_id,
                        messageId: targetAnswer.message_id,
                        feedback: newValue,
                    },
                },
            });
        } catch {
            rollbackAnswerFeedback(messageId, newValue, initialFeedbackValue);
        }
    };

    const updateAnswerFeedback = (messageId: Answer['id'], value: Answer['feedback']) => {
        setAnswers(prev => prev.map(answer => {
            if (answer.id !== messageId) {
                return answer;
            }
            return { ...answer, feedback: value };
        }));
    };

    const rollbackAnswerFeedback = (
        messageId: Answer['id'],
        expectedCurrentValue: Answer['feedback'],
        rollbackValue: Answer['feedback'],
    ) => {
        setAnswers(prev => prev.map(answer => {
            if (answer.id !== messageId || answer.feedback !== expectedCurrentValue) {
                return answer;
            }
            return { ...answer, feedback: rollbackValue };
        }));
    };

    const isResponding = loading || historyLoading || Boolean(answers[answers.length - 1]?.streaming);

    const chatView = (
        <div className="plesk-copilot-chat-view">
            <div className="plesk-copilot-chat-header">
                <Toolbar>
                    <Heading level={2}>
                        <Icon src={require('images/AI-colored.svg')} />{' '}
                        {translate('title')}
                    </Heading>
                    <ToolbarExpander />
                    <Button icon="plus" ghost tooltip={translate('new-chat')} tooltipAsLabel disabled={isResponding} onClick={startNewChat} />
                    <Button icon="clock" ghost tooltip={translate('history')} tooltipAsLabel disabled={isResponding} onClick={() => setIsHistoryOpen(true)} />
                    <Button tooltip={translate('close-button-title')} tooltipAsLabel ghost icon="cross-mark" onClick={() => setIsRightSidebarOpen(false)} />
                </Toolbar>
            </div>
            <div className="plesk-copilot-chat-messages" ref={listRef}>
                {historyLoading && <ContentLoader />}
                {!historyLoading && answers.length === 0 ? (
                    <div className="plesk-copilot-welcome">
                        <Item
                            icon={<Icon src={require('images/AI-colored.svg')} />}
                            title={String(translate('welcome.greeting'))}
                            description={<Paragraph>{translate('welcome.description')}</Paragraph>}
                        />
                        <div className="plesk-copilot-suggestions" data-testid="copilot-suggestions">
                            <Text bold>{translate('welcome.try-asking')}</Text>
                            <div className="plesk-copilot-suggestions__labels">
                                {SUGGESTIONS.map(({ key, icon }) => (
                                    <Label
                                        key={key}
                                        icon={icon}
                                        view="outline"
                                        size="lg"
                                        onClick={() => loadAnswer(String(translate(key)))}
                                    >
                                        {translate(key)}
                                    </Label>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div ref={setContentRef}>
                        {answers.map(message => (
                            <MessageItem
                                key={message.id}
                                message={message}
                                onFeedback={handleFeedback}
                                feedbackDisabled={feedbackDisabled.has(message.id)}
                            />
                        ))}
                        {loading && (
                            <div className="msg-row left">
                                <div className="assistant-card">
                                    <div className="assistant-content">
                                        <ContentLoader
                                            className="plesk-copilot-loading-indicator"
                                            text={<Translate content={'loader.text'} />}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <form
                onSubmit={onSubmit}
                className="plesk-copilot-chat-input-row"
                ref={inputRef}
            >
                <TextArea
                    value={question}
                    placeholder={String(translate('chat-input.placeholder'))}
                    disabled={loading}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey && question.trim()) {
                            e.preventDefault();
                            loadAnswer();
                        }
                    }}
                    size="fill"
                    className="plesk-copilot-chat-input"
                    autoheight
                    rows={1}
                />
                {!!question && (
                    <AiStylePicker
                        enabled={!!config?.copilot?.chat?.useStyles}
                        value={selectedStyle}
                        onChange={setSelectedStyle}
                    />
                )}
                {!!question && <Button ghost icon="arrow-right-circle-filled" type="submit" disabled={loading} />}
            </form>
            <Item
                className="plesk-copilot-privacy"
                icon={<Icon name="shield" size="16" />}
                description={
                    <Translate
                        content="privacy.text"
                        params={{
                            link: <Link href={config?.product.privacyPolicyUrl} target="_blank">{translate('privacy.link')}</Link>,
                        }}
                    />
                }
            />
        </div>
    );

    return (
        <div className="plesk-copilot-chat-container">
            <TransitionGroup component={null}>
                <CSSTransition
                    key={isHistoryOpen ? 'history' : 'chat'}
                    classNames="plesk-copilot-view"
                    timeout={200}
                    exit={false}
                >
                    {isHistoryOpen ? (
                        <ConversationHistory onBack={() => setIsHistoryOpen(false)} />
                    ) : (
                        chatView
                    )}
                </CSSTransition>
            </TransitionGroup>
        </div>
    );
};

export default Chat;
